<script setup lang="ts">
import { ref, watch, onBeforeUnmount, computed, onMounted } from 'vue';
import { usePlaylistStore } from '../stores/playlistStore';
import { useVideoStore } from '../stores/videoStore';
import { usePlayer } from '../composables/usePlayer';

const playlistStore = usePlaylistStore();
const videoStore = useVideoStore();
const videoEl = ref<HTMLVideoElement | null>(null);
const player = usePlayer(videoEl);
const isPaused = ref(true);

const isPlaylistMode = computed(() => !!playlistStore.currentPlaylist);
const currentName = computed(() => {
  if (isPlaylistMode.value && playlistStore.currentEpisode) {
    return `第 ${playlistStore.playingEpisodeIndex + 1} / ${playlistStore.currentPlaylist!.episodes.length} 集`;
  }
  if (playlistStore.playingVideoId) {
    const v = videoStore.filteredVideos.find((x) => x.id === playlistStore.playingVideoId);
    return v?.name || '';
  }
  return '';
});

function playStandaloneVideo(fileId: string, name: string, segmented: boolean) {
  playlistStore.playingVideoId = fileId;
  if (segmented) {
    player.playHls(fileId);
  } else {
    player.playNative(`/video/${fileId}`);
  }
  playlistStore.viewMode = 'player';
}

function playEpisodeFromList(index: number) {
  if (!playlistStore.currentPlaylist?.episodes[index]) return;
  playlistStore.playingEpisodeIndex = index;
  const ep = playlistStore.currentPlaylist.episodes[index];
  playlistStore.playingVideoId = ep.file_id;

  if (ep.segmented) {
    player.playHls(ep.file_id);
  } else {
    player.playNative(ep.url);
  }
}

function playPrev() {
  if (isPlaylistMode.value && playlistStore.playingEpisodeIndex > 0) {
    playlistStore.playEpisode(playlistStore.playingEpisodeIndex - 1);
    return;
  }
  const idx = videoStore.filteredVideos.findIndex((v) => v.id === playlistStore.playingVideoId);
  if (idx > 0) {
    const v = videoStore.filteredVideos[idx - 1];
    playStandaloneVideo(v.id, v.name, !!v.segmented);
  }
}

function playNext() {
  if (isPlaylistMode.value && playlistStore.playingEpisodeIndex < playlistStore.currentPlaylist!.episodes.length - 1) {
    playlistStore.playEpisode(playlistStore.playingEpisodeIndex + 1);
    return;
  }
  const idx = videoStore.filteredVideos.findIndex((v) => v.id === playlistStore.playingVideoId);
  if (idx >= 0 && idx < videoStore.filteredVideos.length - 1) {
    const v = videoStore.filteredVideos[idx + 1];
    playStandaloneVideo(v.id, v.name, !!v.segmented);
  }
}

function goBack() {
  player.destroy();
  playlistStore.goBack();
}

function onEnded() {
  playlistStore.onVideoEnded();
}

// 监听播放/暂停
function onPlay() { isPaused.value = false; }
function onPause() { isPaused.value = true; }

watch(videoEl, (el) => {
  if (el) {
    el.addEventListener('ended', onEnded);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
  }
});

onBeforeUnmount(() => {
  player.destroy();
});

defineExpose({ playStandaloneVideo, playEpisodeFromList, playPrev, playNext, goBack, videoEl });
</script>

<template>
  <div class="player-wrapper" v-if="playlistStore.isPlaying">
    <div class="player-container">
      <video ref="videoEl" controls playsinline class="player-video"></video>

      <!-- 暂停覆盖层 -->
      <div v-if="isPaused" class="pause-overlay" @click="videoEl?.play()">
        <div class="pause-content" @click.stop>
          <h3 class="pause-title">{{ currentName }}</h3>
          <div class="pause-actions">
            <button class="pause-btn" :disabled="!playlistStore.canGoPrev" @click="playPrev()">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
                <path d="m15 18-6-6 6-6"/>
              </svg>
              上一集
            </button>
            <button class="pause-btn primary" @click="videoEl?.play()">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
              继续播放
            </button>
            <button class="pause-btn" :disabled="!playlistStore.canGoNext" @click="playNext()">
              下一集
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
                <path d="m9 18 6-6-6-6"/>
              </svg>
            </button>
          </div>
          <div class="pause-tools">
            <button class="tool-btn" @click="playlistStore.toggleListWhilePlaying()">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
              {{ playlistStore.showListWhilePlaying ? '隐藏列表' : '显示列表' }}
            </button>
            <button class="tool-btn" @click="goBack()">返回列表</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.player-wrapper {
  width: 100%;
}

.player-container {
  position: relative;
  width: 100%;
}

.player-video {
  width: 100%;
  border-radius: var(--radius-lg);
  background: #000;
  display: block;
}

/* ── 暂停覆盖层 ── */
.pause-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  cursor: pointer;
}

.pause-content {
  text-align: center;
  animation: fadeIn 0.2s ease-out;
  cursor: default;
}

.pause-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 24px;
  max-width: 400px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pause-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 20px;
}

.pause-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.1);
  color: var(--text-primary);
  border: 1px solid rgba(255,255,255,0.2);
  padding: 10px 20px;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all var(--transition-fast);
}
.pause-btn:hover:not(:disabled) {
  background: rgba(255,255,255,0.2);
  border-color: rgba(255,255,255,0.3);
}
.pause-btn.primary {
  background: var(--accent-gradient);
  border-color: transparent;
}
.pause-btn.primary:hover {
  box-shadow: 0 0 20px rgba(99, 102, 241, 0.4);
}
.pause-btn:disabled { opacity: 0.3; cursor: not-allowed; }

.pause-tools {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.tool-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  color: var(--text-muted);
  border: none;
  padding: 6px 12px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: 12px;
  transition: color var(--transition-fast);
}
.tool-btn:hover { color: var(--text-primary); }

@media (max-width: 768px) {
  .player-video {
    border-radius: 0;
  }

  .pause-overlay {
    border-radius: 0;
  }

  .pause-title {
    font-size: 15px;
    max-width: 280px;
    margin-bottom: 16px;
  }

  .pause-actions {
    flex-direction: column;
    gap: 8px;
    align-items: center;
  }

  .pause-btn {
    width: 200px;
    justify-content: center;
    padding: 10px 16px;
    font-size: 13px;
  }

  .pause-tools {
    flex-wrap: wrap;
  }
}
</style>
